import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, Bell, CreditCard, HelpCircle, LogOut, ChevronRight, 
  Moon, Sun, Mail, Shield, Watch, Trash2, Camera, Clock, Edit3 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [notifications, setNotifications] = useState({
    push: true,
    email: true,
    sms: false
  });
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    if (user?.notification_preferences) {
      setNotifications(user.notification_preferences);
    }
  }, [user]);

  const handleNotificationChange = async (key, value) => {
    const newPrefs = { ...notifications, [key]: value };
    setNotifications(newPrefs);
    
    try {
      await axios.put(`${API}/user/notifications`, newPrefs);
      updateUser({ notification_preferences: newPrefs });
    } catch (error) {
      toast.error('Failed to update preferences');
      setNotifications(notifications); // Revert
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const menuSections = [
    {
      title: 'Account',
      items: [
        { icon: User, label: 'Edit Profile', onClick: () => navigate('/settings/profile'), testId: 'edit-profile' },
        { icon: Watch, label: 'Connected Devices', onClick: () => navigate('/settings/devices'), testId: 'connected-devices' },
        { icon: Shield, label: 'Privacy & Data', onClick: () => navigate('/settings/privacy'), testId: 'privacy' },
      ]
    },
    {
      title: 'Data Sources',
      items: [
        { icon: Camera, label: 'Run Facial Scan', onClick: () => navigate('/scan/start'), testId: 'run-facial-scan' },
        { icon: Edit3, label: 'Manual Entry', onClick: () => navigate('/manual-entry'), testId: 'manual-entry' },
        { icon: Clock, label: 'Scan History', onClick: () => navigate('/scan/history'), testId: 'scan-history' },
      ]
    },
    {
      title: 'Subscription',
      items: [
        { icon: CreditCard, label: 'Billing & Plan', onClick: () => navigate('/billing'), testId: 'billing' },
      ]
    },
    {
      title: 'Support',
      items: [
        { icon: HelpCircle, label: 'FAQ', onClick: () => navigate('/support/faq'), testId: 'faq' },
        { icon: Mail, label: 'Contact Support', onClick: () => navigate('/support/contact'), testId: 'contact' },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="font-heading font-bold text-lg">Settings</h1>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* User Info */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card p-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-xl font-bold text-primary">
                  {user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="font-heading font-semibold">{user?.name || 'User'}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                user?.subscription_status === 'active' 
                  ? 'bg-[#8BB89F]/20 text-[#8BB89F]' 
                  : 'bg-primary/20 text-primary'
              }`}>
                {user?.subscription_status === 'active' ? 'Premium' : 'Trial'}
              </div>
            </div>
          </Card>
        </motion.section>

        {/* Appearance */}
        <section>
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            Appearance
          </h2>
          <Card className="glass-card">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
                <span>Dark Mode</span>
              </div>
              <Switch 
                checked={theme === 'dark'} 
                onCheckedChange={toggleTheme}
                data-testid="dark-mode-toggle"
              />
            </div>
          </Card>
        </section>

        {/* Notifications */}
        <section>
          <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
            Notifications
          </h2>
          <Card className="glass-card divide-y divide-border">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Bell size={20} />
                <span>Push Notifications</span>
              </div>
              <Switch 
                checked={notifications.push} 
                onCheckedChange={(v) => handleNotificationChange('push', v)}
                data-testid="push-notifications-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Mail size={20} />
                <span>Email Updates</span>
              </div>
              <Switch 
                checked={notifications.email} 
                onCheckedChange={(v) => handleNotificationChange('email', v)}
                data-testid="email-notifications-toggle"
              />
            </div>
          </Card>
        </section>

        {/* Menu Sections */}
        {menuSections.map((section, sIndex) => (
          <section key={section.title}>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              {section.title}
            </h2>
            <Card className="glass-card divide-y divide-border">
              {section.items.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    onClick={item.onClick}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    data-testid={item.testId}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} className="text-muted-foreground" />
                      <span>{item.label}</span>
                    </div>
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>
                );
              })}
            </Card>
          </section>
        ))}

        {/* Logout */}
        <Button
          variant="outline"
          onClick={() => setShowLogoutModal(true)}
          className="w-full rounded-full text-destructive border-destructive/30 hover:bg-destructive/10"
          data-testid="logout-btn"
        >
          <LogOut size={18} className="mr-2" />
          Log Out
        </Button>

        {/* Version */}
        <p className="text-center text-xs text-muted-foreground">
          HealthLink360 v1.0.0
        </p>
      </main>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm"
          >
            <Card className="glass-card p-6 text-center">
              <LogOut size={32} className="text-destructive mx-auto mb-4" />
              <h2 className="font-heading font-bold text-xl mb-2">Log Out?</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to log out of HealthLink360?
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowLogoutModal(false)}
                  className="flex-1 rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleLogout}
                  className="flex-1 bg-destructive hover:bg-destructive/90 rounded-full"
                  data-testid="confirm-logout-btn"
                >
                  Log Out
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
